/**
 * MCP Configuration Service
 * 
 * Manages MCP configuration for organizations in Firestore
 */

import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

// ============================================================================
// Types
// ============================================================================

export interface MCPOrgConfig {
  orgId: string;
  orgName: string;
  
  // MCP Configuration
  mcp: {
    enabled: boolean;
    serverUrl?: string;
    allowedTools: string[]; // ['*'] = all tools, or specific tool names
    allowedResources: string[]; // ['*'] = all resources
  };
  
  // AI Provider Configuration
  aiProviders?: {
    openai?: {
      enabled: boolean;
      apiKey?: string; // Encrypted in production
      model: string;
    };
    claude?: {
      enabled: boolean;
      apiKey?: string;
      model: string;
    };
    custom?: {
      enabled: boolean;
      endpoint: string;
      apiKey?: string;
    };
  };
  
  // Feature Flags
  features?: {
    agentMode: boolean;
    voiceAgent: boolean;
    mcpIntegration: boolean;
    externalTools: boolean; // n8n, Zapier, etc.
  };
  
  // Rate Limiting
  limits?: {
    requestsPerMinute: number;
    requestsPerDay: number;
    maxToolExecutionTime: number; // seconds
  };
  
  // Metadata
  updatedAt?: Date;
  updatedBy?: string;
}

export interface MCPConfigUpdate {
  mcp?: {
    enabled?: boolean;
    allowedTools?: string[];
    allowedResources?: string[];
  };
  aiProviders?: MCPOrgConfig['aiProviders'];
  features?: MCPOrgConfig['features'];
  limits?: MCPOrgConfig['limits'];
}

// ============================================================================
// Default Configuration
// ============================================================================

export const DEFAULT_MCP_CONFIG: Partial<MCPOrgConfig> = {
  mcp: {
    enabled: true,
    allowedTools: ['*'], // All tools allowed by default
    allowedResources: ['*'] // All resources allowed by default
  },
  features: {
    agentMode: true,
    voiceAgent: true,
    mcpIntegration: true,
    externalTools: true
  },
  limits: {
    requestsPerMinute: 60,
    requestsPerDay: 10000,
    maxToolExecutionTime: 30
  }
};

// ============================================================================
// Service Class
// ============================================================================

class MCPConfigService {
  /**
   * Get MCP configuration for an organization
   */
  async getOrgConfig(orgId: string): Promise<MCPOrgConfig | null> {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists()) {
        console.warn(`Organization not found: ${orgId}`);
        return null;
      }
      
      const data = orgDoc.data();
      
      // Return config with defaults merged in
      return {
        orgId,
        orgName: data.name || 'Unknown Organization',
        mcp: {
          enabled: data.mcp?.enabled ?? DEFAULT_MCP_CONFIG.mcp!.enabled,
          serverUrl: data.mcp?.serverUrl,
          allowedTools: data.mcp?.allowedTools || DEFAULT_MCP_CONFIG.mcp!.allowedTools,
          allowedResources: data.mcp?.allowedResources || DEFAULT_MCP_CONFIG.mcp!.allowedResources
        },
        aiProviders: data.aiProviders || {},
        features: {
          agentMode: data.features?.agentMode ?? DEFAULT_MCP_CONFIG.features!.agentMode,
          voiceAgent: data.features?.voiceAgent ?? DEFAULT_MCP_CONFIG.features!.voiceAgent,
          mcpIntegration: data.features?.mcpIntegration ?? DEFAULT_MCP_CONFIG.features!.mcpIntegration,
          externalTools: data.features?.externalTools ?? DEFAULT_MCP_CONFIG.features!.externalTools
        },
        limits: {
          requestsPerMinute: data.limits?.requestsPerMinute || DEFAULT_MCP_CONFIG.limits!.requestsPerMinute,
          requestsPerDay: data.limits?.requestsPerDay || DEFAULT_MCP_CONFIG.limits!.requestsPerDay,
          maxToolExecutionTime: data.limits?.maxToolExecutionTime || DEFAULT_MCP_CONFIG.limits!.maxToolExecutionTime
        },
        updatedAt: data.updatedAt?.toDate(),
        updatedBy: data.updatedBy
      };
    } catch (error) {
      console.error('Error fetching MCP config:', error);
      throw error;
    }
  }
  
  /**
   * Update MCP configuration for an organization
   */
  async updateOrgConfig(
    orgId: string,
    updates: MCPConfigUpdate,
    userId: string
  ): Promise<void> {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists()) {
        throw new Error(`Organization not found: ${orgId}`);
      }
      
      // Prepare update data
      const updateData: any = {
        updatedAt: Timestamp.now(),
        updatedBy: userId
      };
      
      // Update MCP settings
      if (updates.mcp) {
        if (updates.mcp.enabled !== undefined) {
          updateData['mcp.enabled'] = updates.mcp.enabled;
        }
        if (updates.mcp.allowedTools !== undefined) {
          updateData['mcp.allowedTools'] = updates.mcp.allowedTools;
        }
        if (updates.mcp.allowedResources !== undefined) {
          updateData['mcp.allowedResources'] = updates.mcp.allowedResources;
        }
      }
      
      // Update AI providers
      if (updates.aiProviders) {
        updateData.aiProviders = updates.aiProviders;
      }
      
      // Update features
      if (updates.features) {
        updateData.features = updates.features;
      }
      
      // Update limits
      if (updates.limits) {
        updateData.limits = updates.limits;
      }
      
      await updateDoc(orgRef, updateData);
      
      console.log('✅ MCP config updated for org:', orgId);
    } catch (error) {
      console.error('Error updating MCP config:', error);
      throw error;
    }
  }
  
  /**
   * Initialize MCP configuration for a new organization
   */
  async initializeOrgConfig(orgId: string, orgName: string): Promise<void> {
    try {
      const orgRef = doc(db, 'organizations', orgId);
      const orgDoc = await getDoc(orgRef);
      
      // Only initialize if org doesn't exist or doesn't have MCP config
      if (!orgDoc.exists() || !orgDoc.data()?.mcp) {
        const data: any = {
          updatedAt: Timestamp.now()
        };
        
        if (!orgDoc.exists()) {
          data.name = orgName;
          data.createdAt = Timestamp.now();
        }
        
        // Add default MCP config
        data.mcp = DEFAULT_MCP_CONFIG.mcp;
        data.features = DEFAULT_MCP_CONFIG.features;
        data.limits = DEFAULT_MCP_CONFIG.limits;
        
        if (!orgDoc.exists()) {
          await setDoc(orgRef, data);
        } else {
          await updateDoc(orgRef, data);
        }
        
        console.log('✅ MCP config initialized for org:', orgId);
      }
    } catch (error) {
      console.error('Error initializing MCP config:', error);
      throw error;
    }
  }
  
  /**
   * Enable/Disable MCP for an organization
   */
  async toggleMCP(orgId: string, enabled: boolean, userId: string): Promise<void> {
    await this.updateOrgConfig(orgId, {
      mcp: { enabled }
    }, userId);
  }
  
  /**
   * Update allowed tools
   */
  async updateAllowedTools(orgId: string, tools: string[], userId: string): Promise<void> {
    await this.updateOrgConfig(orgId, {
      mcp: { allowedTools: tools }
    }, userId);
  }
  
  /**
   * Update rate limits
   */
  async updateRateLimits(
    orgId: string,
    limits: { requestsPerMinute?: number; requestsPerDay?: number },
    userId: string
  ): Promise<void> {
    const currentConfig = await this.getOrgConfig(orgId);
    if (!currentConfig) {
      throw new Error('Organization not found');
    }
    
    await this.updateOrgConfig(orgId, {
      limits: {
        ...currentConfig.limits!,
        ...limits
      }
    }, userId);
  }
  
  /**
   * Check if MCP is enabled for an organization
   */
  async isMCPEnabled(orgId: string): Promise<boolean> {
    try {
      const config = await this.getOrgConfig(orgId);
      return config?.mcp?.enabled ?? true; // Default to true
    } catch (error) {
      console.error('Error checking MCP status:', error);
      return false;
    }
  }
  
  /**
   * Get available tools for an organization
   */
  async getAllowedTools(orgId: string): Promise<string[]> {
    try {
      const config = await this.getOrgConfig(orgId);
      return config?.mcp?.allowedTools || ['*'];
    } catch (error) {
      console.error('Error fetching allowed tools:', error);
      return ['*'];
    }
  }
  
  /**
   * Validate if a specific tool is allowed
   */
  async isToolAllowed(orgId: string, toolName: string): Promise<boolean> {
    const allowedTools = await this.getAllowedTools(orgId);
    
    // If wildcard, all tools are allowed
    if (allowedTools.includes('*')) {
      return true;
    }
    
    // Check if specific tool is in the list
    return allowedTools.includes(toolName);
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const mcpConfigService = new MCPConfigService();

// ============================================================================
// Available MCP Tools (for UI selection)
// ============================================================================

export interface ToolDefinition {
  name: string;
  category: string;
  description: string;
  dangerLevel: 'safe' | 'moderate' | 'dangerous';
}

export const AVAILABLE_MCP_TOOLS: ToolDefinition[] = [
  // Lead Management
  { name: 'create_lead', category: 'Leads', description: 'Create a new lead', dangerLevel: 'safe' },
  { name: 'list_leads', category: 'Leads', description: 'List all leads', dangerLevel: 'safe' },
  { name: 'update_lead', category: 'Leads', description: 'Update lead information', dangerLevel: 'moderate' },
  { name: 'get_lead', category: 'Leads', description: 'Get single lead details', dangerLevel: 'safe' },
  { name: 'delete_lead', category: 'Leads', description: 'Delete a lead', dangerLevel: 'dangerous' },
  { name: 'search_leads', category: 'Leads', description: 'Search leads', dangerLevel: 'safe' },
  
  // Task Management
  { name: 'create_task', category: 'Tasks', description: 'Create a new task', dangerLevel: 'safe' },
  { name: 'list_tasks', category: 'Tasks', description: 'List all tasks', dangerLevel: 'safe' },
  { name: 'update_task', category: 'Tasks', description: 'Update task information', dangerLevel: 'moderate' },
  { name: 'complete_task', category: 'Tasks', description: 'Mark task as completed', dangerLevel: 'safe' },
  { name: 'delete_task', category: 'Tasks', description: 'Delete a task', dangerLevel: 'dangerous' },
  
  // Opportunity Management
  { name: 'create_opportunity', category: 'Opportunities', description: 'Create new opportunity', dangerLevel: 'safe' },
  { name: 'list_opportunities', category: 'Opportunities', description: 'List opportunities', dangerLevel: 'safe' },
  { name: 'update_opportunity', category: 'Opportunities', description: 'Update opportunity', dangerLevel: 'moderate' },
  { name: 'delete_opportunity', category: 'Opportunities', description: 'Delete opportunity', dangerLevel: 'dangerous' },
  
  // Company Management
  { name: 'create_company', category: 'Companies', description: 'Create a new company', dangerLevel: 'safe' },
  { name: 'list_companies', category: 'Companies', description: 'List all companies', dangerLevel: 'safe' },
  { name: 'update_company', category: 'Companies', description: 'Update company info', dangerLevel: 'moderate' },
  { name: 'delete_company', category: 'Companies', description: 'Delete company', dangerLevel: 'dangerous' },
  
  // Contact Management
  { name: 'create_contact', category: 'Contacts', description: 'Create a new contact', dangerLevel: 'safe' },
  { name: 'list_contacts', category: 'Contacts', description: 'List all contacts', dangerLevel: 'safe' },
  { name: 'update_contact', category: 'Contacts', description: 'Update contact info', dangerLevel: 'moderate' },
  { name: 'delete_contact', category: 'Contacts', description: 'Delete contact', dangerLevel: 'dangerous' }
];

export const TOOL_CATEGORIES = Array.from(
  new Set(AVAILABLE_MCP_TOOLS.map(t => t.category))
);

